import { SafeError, SafePromise } from '@benzinga/safe-await';
import camelcaseKeys from 'camelcase-keys';
import { camelCaseKeysShallow, formatImageUrl } from '@benzinga/utils';
import { ArticleManager } from '../manager';
import { isSponsoredArticle } from '../sponsored-utils';
import { findPrimaryImage } from './misc';
import { getArticleTerms } from './article-terms';
import { replaceRedditHTML, printProHeadlineArticleContent } from './content';
import { getQuoteDetailsFromSymbols } from './meta';
import { articlesMetaInfo } from './meta';
import type { ArticleData, GetArticleI } from '../entities/article';
import type { Term } from '@benzinga/content-manager';
import type { Session } from '@benzinga/session';

export const camelcaseKeysArticleData = camelcaseKeys as (
  input: GetArticleI,
  options?: Parameters<typeof camelcaseKeys>[1],
) => ArticleData;

export const getArticle = async (session: Session, node_id: number | string): SafePromise<GetArticleI> => {
  const articleRes = await session.getManager(ArticleManager).getInternalNode(node_id);

  if (articleRes.ok) {
    const article = articleRes.ok as GetArticleI;

    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const isImageExpired = new Date(article.CreatedAt ?? Date.now()) < oneYearAgo;

    if (!isImageExpired) {
      article.image = findPrimaryImage(article.assets);
      article.image = formatImageUrl({ image: article.image }, true);
    }

    // if (!article.image) {
    //   console.error('WARNING::Primary Image not found or not set!', {
    //     assets: article.assets,
    //     node_id,
    //   });
    // }

    article.Body = replaceRedditHTML(article.Body);

    // Fix anchor hrefs starting with 'www.' to 'https://www.'
    if (typeof article.Body === 'string') {
      article.Body = article.Body.replace(/(<a\b[^>]*?href=["'])www\./gi, '$1https://www.');
      article.Body = article.Body.replace(
        /(<a\b[^>]*?href=)(["'])((?![/#]|https?:)[a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z0-9][-a-zA-Z0-9.]*(?:\/[^"']*)?)(\2)/gi,
        '$1$2https://$3$4',
      );
    }
    const sponsored = isSponsoredArticle(camelCaseKeysShallow(article) as ArticleData);

    article.isHeadline = !article.Body || (article.IsBzProPost && !article.IsBzPost);

    const MAX_ARTICLE_LENGTH_FOR_PARSER = 1000000;
    // If article weight is more than 1mb display it in frame
    article.shouldDisplayedInFrame = !article.isHeadline && article?.Body?.length > MAX_ARTICLE_LENGTH_FOR_PARSER;

    // If pr_secfilings appears in article then it's old, should avoid to parse that type
    const shouldParseBlocks =
      !article.isHeadline && article.Type !== 'pr_secfilings' && !article.shouldDisplayedInFrame;

    if (shouldParseBlocks) {
      const { parseArticleBodyToBlocks, splitHTMLContent } = await import('./parsing');
      article.blocks = await parseArticleBodyToBlocks(session, article.Body, sponsored, article.NodeID);
      article.parsedBody = await splitHTMLContent(article.Body);
    } else {
      article.blocks = [];
      article.parsedBody = [];
    }

    if (article.isHeadline) {
      article.Body = printProHeadlineArticleContent();
    }

    if (article.UserID) {
      const authorRes = await session.getManager(ArticleManager).getAuthor(article.UserID);

      article.Author = authorRes?.ok ?? null;
      if (article.Author && article.Author.uid && article.Author.profileUrl === 'https://www.benzinga.com/author/') {
        article.Author.profileUrl = `https://www.benzinga.com/user/${article.Author.uid}`;
      } else if (article.Author && article.Author.profileUrl) {
        article.Author.profileUrl = article.Author.profileUrl?.replace('https://www.benzinga.com/', '/');
      }
    }

    try {
      const symbols = article?.Tickers?.map(ticker => ticker?.name) ?? [];
      if (Array.isArray(symbols)) {
        const quoteDetails = await getQuoteDetailsFromSymbols(symbols);

        if (quoteDetails) {
          article.Quotes = quoteDetails;
        }
      }
    } catch (e) {
      console.error('Article Get Quotes Error:', e);
    }

    const termsReq = await getArticleTerms(session, article);
    article.Terms = termsReq.ok as Term[];

    return {
      ok: article,
    };
  } else if (articleRes.err) {
    return { err: articleRes.err };
  } else {
    return { err: new SafeError('Something went wrong!', 'articleRequest') };
  }
};

export const getArticleData = async (node_id: number | string, session: Session): SafePromise<ArticleData> => {
  if (node_id === 'undefined') {
    return { err: new SafeError('node_id is undefined', 'getArticleData') };
  }
  const articleRes = await getArticle(session, node_id);
  if (articleRes?.ok?.blocks?.length) {
    const { enrichParsedArticleBlocks } = await import('@benzinga/blocks-utils/enrich');
    articleRes.ok.blocks = await enrichParsedArticleBlocks(session, articleRes.ok.blocks);
  }
  const articleData: ArticleData | undefined = articleRes?.ok && camelcaseKeysArticleData(articleRes.ok);
  if (articleData) {
    articleData.metaProps = await articlesMetaInfo(articleData);
    return { ok: articleData };
  } else {
    return { err: new SafeError(articleRes?.err?.message ?? 'Something went wrong', 'getArticleData'), ok: undefined };
  }
};

export const getInitialTickers = (article: ArticleData): string[] => {
  if (!Array.isArray(article.quotes)) return [];
  return article.quotes?.filter(ticker => ticker.type === 'STOCK').map(item => item.symbol);
};
